import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Brain, BookOpen, Puzzle, Wrench, Play, Activity, BarChart3,
  ChevronRight, Clock, AlertCircle, Loader, Zap, ArrowRight,
  Plane, TrendingUp, DollarSign, Globe, CheckCircle, Trophy, Upload,
  Target,
} from 'lucide-react'
import type { Competency, Task, Tool, Trace } from '../engine/types'
import { listCompetencies } from '../engine/CompetencyStudio'
import { listTasks } from '../engine/TaskBuilder'
import { listTools } from '../engine/ToolRegistry'
import { listTraces } from '../engine/TraceRecorder'
import { useLang } from '../i18n/LangContext'
import type { TranslationKey } from '../i18n/translations'

interface StatCard {
  label: string
  value: number
  icon: React.ReactNode
  accent: string
}

interface PageLink {
  path: string
  label: TranslationKey
  desc: string
  icon: React.ReactNode
}

interface DomainDef {
  icon: typeof Plane
  nameZh: string
  nameEn: string
  descZh: string
  descEn: string
  matchKeys: string[]
  accent: string
}

interface Differentiator {
  icon: typeof Globe
  titleKey: TranslationKey
  descKey: TranslationKey
  accent: string
}

function formatTimeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffSec = Math.floor(diffMs / 1000)
  if (diffSec < 60) return `${diffSec}s ago`
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  return `${diffDay}d ago`
}

function getStatusBadgeStyle(status: string) {
  switch (status) {
    case 'completed':
      return { bg: 'rgba(74,222,128,0.12)', text: 'var(--color-success)', label: 'Completed' }
    case 'running':
      return { bg: 'rgba(108,140,255,0.12)', text: 'var(--color-accent)', label: 'Running' }
    case 'error':
      return { bg: 'rgba(248,113,113,0.12)', text: 'var(--color-danger)', label: 'Error' }
    case 'timeout':
      return { bg: 'rgba(251,191,36,0.12)', text: 'var(--color-warning)', label: 'Timeout' }
    default:
      return { bg: 'rgba(90,94,120,0.12)', text: 'var(--color-text-muted)', label: status }
  }
}

export default function HomePage() {
  const { t, lang } = useLang()
  const [competencies, setCompetencies] = useState<Competency[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [tools, setTools] = useState<Tool[]>([])
  const [traces, setTraces] = useState<Trace[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const pageLinks: PageLink[] = [
    { path: '/competencies', label: 'nav.competency', desc: lang === 'zh' ? '管理能力模型与维度' : 'Manage competency models and dimensions', icon: <BookOpen size={22} /> },
    { path: '/tasks', label: 'nav.tasks', desc: lang === 'zh' ? '创建和管理评测任务' : 'Create and manage assessment tasks', icon: <Puzzle size={22} /> },
    { path: '/tools', label: 'nav.tools', desc: lang === 'zh' ? '注册和配置Agent工具' : 'Register and configure agent tools', icon: <Wrench size={22} /> },
    { path: '/run', label: 'nav.run', desc: lang === 'zh' ? '执行Agent任务' : 'Execute agents against tasks', icon: <Play size={22} /> },
    { path: '/traces', label: 'nav.traces', desc: lang === 'zh' ? '查看Agent执行轨迹' : 'Inspect agent execution traces', icon: <Activity size={22} /> },
    { path: '/reports', label: 'nav.reports', desc: lang === 'zh' ? '查看评分与分析报告' : 'View scoring and analysis reports', icon: <BarChart3 size={22} /> },
  ]

  const domainDefs: DomainDef[] = [
    {
      icon: Plane,
      nameZh: '旅游规划',
      nameEn: 'Travel Planning',
      descZh: '行程编排、预算控制与多约束优化',
      descEn: 'Itinerary planning, budget control and multi-constraint optimization',
      matchKeys: ['travel', 'tourism', 'trip', '旅游', '行程'],
      accent: 'var(--color-info)',
    },
    {
      icon: TrendingUp,
      nameZh: '商业战略',
      nameEn: 'Business Strategy',
      descZh: '市场分析、竞争策略与增长决策',
      descEn: 'Market analysis, competitive strategy and growth decisions',
      matchKeys: ['business', 'strategy', 'market', '商业', '战略'],
      accent: 'var(--color-accent)',
    },
    {
      icon: DollarSign,
      nameZh: '财务规划',
      nameEn: 'Financial Planning',
      descZh: '资产配置、风险评估与投资组合',
      descEn: 'Asset allocation, risk assessment and portfolio management',
      matchKeys: ['finance', 'financial', 'investment', '财务', '投资'],
      accent: 'var(--color-success)',
    },
  ]

  const differentiators: Differentiator[] = [
    { icon: Globe, titleKey: 'home.diff.broad', descKey: 'home.diff.broadDesc', accent: 'var(--color-accent)' },
    { icon: CheckCircle, titleKey: 'home.diff.verifiable', descKey: 'home.diff.verifiableDesc', accent: 'var(--color-success)' },
    { icon: Clock, titleKey: 'home.diff.long', descKey: 'home.diff.longDesc', accent: 'var(--color-warning)' },
    { icon: TrendingUp, titleKey: 'home.diff.economic', descKey: 'home.diff.economicDesc', accent: 'var(--color-info)' },
  ]

  useEffect(() => {
    let cancelled = false
    async function fetchData() {
      try {
        setLoading(true)
        setError(null)
        const [compRes, taskRes, toolRes, traceRes] = await Promise.all([
          listCompetencies(),
          listTasks(),
          listTools(),
          listTraces(),
        ])
        if (cancelled) return
        setCompetencies(compRes)
        setTasks(taskRes)
        setTools(toolRes)
        setTraces(traceRes)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchData()
    return () => { cancelled = true }
  }, [])

  const activeTools = tools.filter(t => t.status === 'active').length
  const sessionsRun = traces.length

  const stats: StatCard[] = [
    { label: t('home.stat.competencies'), value: competencies.length, icon: <BookOpen size={18} />, accent: 'var(--color-accent)' },
    { label: t('home.stat.tasks'), value: tasks.length, icon: <Puzzle size={18} />, accent: 'var(--color-success)' },
    { label: t('home.stat.tools'), value: activeTools, icon: <Wrench size={18} />, accent: 'var(--color-warning)' },
    { label: t('home.stat.sessions'), value: sessionsRun, icon: <Zap size={18} />, accent: 'var(--color-info)' },
  ]

  // Compute task count per domain by matching competency domain_id against keywords
  const domainTaskCounts = domainDefs.map((domain) => {
    const matchedCompIds = competencies
      .filter((c) => {
        const haystack = `${c.domain_id} ${c.name} ${c.name_cn}`.toLowerCase()
        return domain.matchKeys.some((k) => haystack.includes(k.toLowerCase()))
      })
      .map((c) => c.id)
    return tasks.filter((task) => matchedCompIds.includes(task.competency_id)).length
  })

  const recentTraces = [...traces]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 8)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader size={32} className="animate-spin" style={{ color: 'var(--color-accent)' }} />
          <span style={{ color: 'var(--color-text-secondary)' }}>{t('common.loading')}</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3 max-w-md text-center p-6 rounded-xl" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}>
          <AlertCircle size={32} style={{ color: 'var(--color-danger)' }} />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t('common.error')}</h2>
          <p style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* ===== 1. Hero Section ===== */}
      <section
        className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-16 sm:py-20 lg:py-24"
        style={{
          background:
            'radial-gradient(1200px 500px at 20% -10%, rgba(108,140,255,0.18), transparent 60%), ' +
            'radial-gradient(900px 500px at 90% 10%, rgba(74,222,128,0.10), transparent 55%), ' +
            'linear-gradient(180deg, var(--color-bg-secondary) 0%, var(--color-bg-primary) 100%)',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <div className="container-page">
          {/* Brand badge */}
          <div className="flex justify-center mb-6">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
              style={{
                backgroundColor: 'var(--color-accent-glow)',
                color: 'var(--color-accent)',
                border: '1px solid rgba(108,140,255,0.25)',
              }}
            >
              <Brain size={14} />
              <span>{lang === 'zh' ? 'AI Agent 评估平台' : 'AI Agent Evaluation Platform'}</span>
            </div>
          </div>

          {/* Title */}
          <h1
            className="text-center text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-4"
            style={{
              color: 'var(--color-text-primary)',
              backgroundImage: 'linear-gradient(135deg, var(--color-text-primary) 0%, var(--color-accent) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            DecisionCraft
          </h1>

          {/* Tagline */}
          <p
            className="text-center text-xl sm:text-2xl font-semibold mb-5"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {t('home.subtitle')}
          </p>

          {/* Description */}
          <div
            className="text-center text-base sm:text-lg max-w-3xl mx-auto mb-8 leading-relaxed"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <p>{lang === 'zh' ? '衡量AI Agent在真实职业场景中的决策能力。' : 'Measuring AI agent decision-making in real professional scenarios.'}</p>
            <p className="mt-1">{lang === 'zh' ? '覆盖旅游、商业、财务等多个领域，基于专家定义的评分标准，提供可验证的能力评估。' : 'Covering tourism, business, finance and more, with expert-defined rubrics and verifiable assessments.'}</p>
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-12">
            <Link
              to="/tasks/contribute"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all hover:translate-y-[-1px] w-full sm:w-auto justify-center"
              style={{
                backgroundColor: 'var(--color-accent)',
                color: '#fff',
                boxShadow: '0 8px 24px rgba(108,140,255,0.35)',
              }}
            >
              <Upload size={16} />
              <span>{t('home.contribute')}</span>
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/leaderboard"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all hover:translate-y-[-1px] w-full sm:w-auto justify-center"
              style={{
                backgroundColor: 'var(--color-bg-card)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-border)',
              }}
            >
              <Trophy size={16} />
              <span>{t('home.viewLeaderboard')}</span>
            </Link>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl p-5 border transition-colors"
                style={{
                  backgroundColor: 'rgba(30,33,50,0.6)',
                  borderColor: 'var(--color-border)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs sm:text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>{stat.label}</span>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat.accent}18` }}>
                    <span style={{ color: stat.accent }}>{stat.icon}</span>
                  </div>
                </div>
                <p className="text-3xl sm:text-4xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 2. Domain Showcase ===== */}
      <section className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-14">
        <div className="container-page">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                {t('home.domains')}
              </h2>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                {lang === 'zh' ? '覆盖多个真实职业场景，持续扩展中' : 'Covering multiple real professional scenarios, expanding continuously'}
              </p>
            </div>
            <Link
              to="/tasks"
              className="hidden sm:inline-flex items-center gap-1 text-sm font-medium transition-colors hover:opacity-80"
              style={{ color: 'var(--color-accent)' }}
            >
              {lang === 'zh' ? '浏览全部任务' : 'Browse all tasks'}
              <ArrowRight size={14} />
            </Link>
          </div>

          {/* Horizontal scroll on mobile, grid on desktop */}
          <div className="flex gap-4 overflow-x-auto pb-2 lg:grid lg:grid-cols-3 lg:overflow-visible">
            {domainDefs.map((domain, idx) => {
              const Icon = domain.icon
              const taskCount = domainTaskCounts[idx]
              return (
                <div
                  key={domain.nameEn}
                  className="min-w-[260px] lg:min-w-0 rounded-xl p-6 border transition-all hover:translate-y-[-2px] flex-shrink-0"
                  style={{
                    backgroundColor: 'var(--color-bg-card)',
                    borderColor: 'var(--color-border)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = domain.accent }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${domain.accent}18` }}
                  >
                    <Icon size={24} style={{ color: domain.accent }} />
                  </div>
                  <h3 className="text-lg font-semibold mb-0.5" style={{ color: 'var(--color-text-primary)' }}>
                    {lang === 'zh' ? domain.nameZh : domain.nameEn}
                  </h3>
                  <p className="text-xs mb-3" style={{ color: 'var(--color-text-muted)' }}>
                    {lang === 'zh' ? domain.nameEn : domain.nameZh}
                  </p>
                  <p className="text-sm mb-4 leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                    {lang === 'zh' ? domain.descZh : domain.descEn}
                  </p>
                  <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    <Target size={12} />
                    <span>{taskCount} {lang === 'zh' ? '个任务' : 'tasks'}</span>
                  </div>
                </div>
              )
            })}
          </div>

          <Link
            to="/tasks"
            className="sm:hidden inline-flex items-center gap-1 text-sm font-medium mt-4"
            style={{ color: 'var(--color-accent)' }}
          >
            {lang === 'zh' ? '浏览全部任务' : 'Browse all tasks'}
            <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* ===== 3. Key Differentiators ===== */}
      <section
        className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-14"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          borderTop: '1px solid var(--color-border)',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <div className="container-page">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              {t('home.differentiators')}
            </h2>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {lang === 'zh' ? '为什么选择 DecisionCraft' : 'What makes DecisionCraft different'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {differentiators.map((diff) => {
              const Icon = diff.icon
              return (
                <div
                  key={diff.titleKey}
                  className="rounded-xl p-6 border transition-all hover:translate-y-[-2px]"
                  style={{
                    backgroundColor: 'var(--color-bg-card)',
                    borderColor: 'var(--color-border)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = diff.accent }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
                >
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${diff.accent}18` }}
                  >
                    <Icon size={22} style={{ color: diff.accent }} />
                  </div>
                  <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                    {t(diff.titleKey)}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                    {t(diff.descKey)}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ===== 4. Quick Links ===== */}
      <section className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-14">
        <div className="container-page">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
            {t('home.quickLinks')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pageLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="rounded-xl p-5 border transition-all hover:translate-y-[-2px] group"
                style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-accent)'; e.currentTarget.style.boxShadow = '0 0 20px var(--color-accent-glow)' }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.boxShadow = 'none' }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--color-accent-glow)' }}>
                      <span style={{ color: 'var(--color-accent)' }}>{link.icon}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>{t(link.label)}</h3>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{link.desc}</p>
                    </div>
                  </div>
                  <ChevronRight size={16} className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--color-accent)' }} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 5. Recent Activity ===== */}
      <section className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 pb-14">
        <div className="container-page">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
              {t('home.recentActivity')}
            </h2>
            <Link
              to="/traces"
              className="inline-flex items-center gap-1 text-sm font-medium transition-colors hover:opacity-80"
              style={{ color: 'var(--color-accent)' }}
            >
              {lang === 'zh' ? '查看全部' : 'View all'}
              <ArrowRight size={14} />
            </Link>
          </div>
          {recentTraces.length === 0 ? (
            <div className="rounded-xl border p-8 text-center" style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}>
              <Activity size={28} style={{ color: 'var(--color-text-muted)', margin: '0 auto' }} />
              <p className="mt-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>{t('home.noTraces')}</p>
            </div>
          ) : (
            <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}>
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <th className="text-left px-5 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Agent</th>
                    <th className="text-left px-5 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Type</th>
                    <th className="text-left px-5 py-3 text-xs font-medium uppercase tracking-wider hidden sm:table-cell" style={{ color: 'var(--color-text-muted)' }}>Steps</th>
                    <th className="text-left px-5 py-3 text-xs font-medium uppercase tracking-wider hidden sm:table-cell" style={{ color: 'var(--color-text-muted)' }}>Duration</th>
                    <th className="text-left px-5 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Status</th>
                    <th className="text-right px-5 py-3 text-xs font-medium uppercase tracking-wider hidden md:table-cell" style={{ color: 'var(--color-text-muted)' }}>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTraces.map((trace, idx) => {
                    const badge = getStatusBadgeStyle(trace.status)
                    const isLast = idx === recentTraces.length - 1
                    return (
                      <tr key={trace.trace_id} style={{ borderBottom: isLast ? 'none' : '1px solid var(--color-border)' }}>
                        <td className="px-5 py-3">
                          <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{trace.agent_name}</span>
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>{trace.agent_type}</span>
                        </td>
                        <td className="px-5 py-3 hidden sm:table-cell">
                          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{trace.steps_count}</span>
                        </td>
                        <td className="px-5 py-3 hidden sm:table-cell">
                          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{(trace.duration_ms / 1000).toFixed(1)}s</span>
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: badge.bg, color: badge.text }}>{badge.label}</span>
                        </td>
                        <td className="px-5 py-3 text-right hidden md:table-cell">
                          <div className="flex items-center justify-end gap-1.5">
                            <Clock size={12} style={{ color: 'var(--color-text-muted)' }} />
                            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{formatTimeAgo(trace.created_at)}</span>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
